import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Loader2, AlertTriangle, Info } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { skillsService } from "@/services/skillsService";

interface EditHelpRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: {
    id: string;
    title: string;
    description: string;
    skills_needed: string[];
    priority: string;
    status: string;
  } | null;
  onUpdate: () => void;
}

const SKILL_SUGGESTIONS = [
  "React", "TypeScript", "Python", "Data Analysis", "Project Management",
  "UI/UX Design", "API Design", "Database", "DevOps", "Marketing",
  "Sales", "Strategy", "Communication", "Leadership", "Finance"
];

export default function EditHelpRequestModal({ 
  isOpen, 
  onClose, 
  request,
  onUpdate 
}: EditHelpRequestModalProps) {
  const { user } = useUser();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [status, setStatus] = useState("open");
  const [skillsNeeded, setSkillsNeeded] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState("");

  useEffect(() => {
    if (request && isOpen) {
      setTitle(request.title);
      setDescription(request.description);
      setPriority(request.priority);
      setStatus(request.status);
      setSkillsNeeded(request.skills_needed || []);
    }
  }, [request, isOpen]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPriority("normal");
    setStatus("open");
    setSkillsNeeded([]);
    setNewSkill("");
  };

  const addSkill = (skill: string) => {
    if (skill && !skillsNeeded.includes(skill)) {
      setSkillsNeeded([...skillsNeeded, skill]);
      setNewSkill("");
    }
  };

  const removeSkill = (skill: string) => {
    setSkillsNeeded(skillsNeeded.filter(s => s !== skill));
  };

  const handleSubmit = async () => {
    if (!user?.id || !request) return;
    if (!title.trim() || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if this is a formal request or check-in based
      if (request.id.startsWith('checkin-')) {
        toast.error("Cannot edit check-in based help requests");
        onClose();
        return;
      }

      // Update the help request via skillsService
      await skillsService.updateHelpRequest(request.id, {
        title: title.trim(),
        description: description.trim(),
        skillsNeeded: skillsNeeded,
        priority: priority as 'low' | 'normal' | 'high' | 'urgent',
        status: status as 'open' | 'in_progress' | 'resolved' | 'expired',
      });

      toast.success("Help request updated successfully!");
      onUpdate();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error updating help request:', error);
      toast.error('Failed to update help request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredSuggestions = SKILL_SUGGESTIONS.filter(
    skill => 
      !skillsNeeded.includes(skill) &&
      skill.toLowerCase().includes(newSkill.toLowerCase())
  );

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Help Request</DialogTitle>
          <DialogDescription>
            Update your help request details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">What do you need help with? *</Label>
            <Input
              id="title"
              placeholder="e.g., Stripe API integration issues"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground text-right">
              {title.length}/100
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Describe your request *</Label>
            <Textarea
              id="description"
              placeholder="Provide context about what you're trying to achieve and where you're stuck..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/500
            </p>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open - Still need help</SelectItem>
                <SelectItem value="in_progress">In Progress - Getting help</SelectItem>
                <SelectItem value="resolved">Resolved - Got the help needed</SelectItem>
                <SelectItem value="expired">Expired - No longer needed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500" />
                    <span>Low - Can wait a few days</span>
                  </div>
                </SelectItem>
                <SelectItem value="normal">
                  <div className="flex items-center gap-2">
                    <span>Normal - Within 1-2 days</span>
                  </div>
                </SelectItem>
                <SelectItem value="high">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <span>High - Need help soon</span>
                  </div>
                </SelectItem>
                <SelectItem value="urgent">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span>Urgent - Blocking work</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Skills Needed */}
          <div className="space-y-2">
            <Label>Skills needed (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Add relevant skills to help us find the right people
            </p>
            
            <div className="flex gap-2">
              <Input
                placeholder="Type a skill..."
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill(newSkill);
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addSkill(newSkill)}
                disabled={!newSkill.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Skill Suggestions */}
            {newSkill && filteredSuggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg bg-muted/50">
                {filteredSuggestions.slice(0, 5).map((skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground text-xs"
                    onClick={() => addSkill(skill)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {skill}
                  </Badge>
                ))}
              </div>
            )}

            {/* Selected Skills */}
            {skillsNeeded.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 border rounded-lg">
                {skillsNeeded.map((skill) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !title.trim() || !description.trim()}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Request'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}